import { CustomImage, CustomModal, CustomTable, Text, useText } from '@src/components';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/myOrders/orderSummaryBalanceModal.scss';
import React, { useCallback, useEffect } from 'react';
import { moneyFormat } from '@src/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { orderDetailsGetPageList } from '@src/store/slice/orderSlice';
import { monthsList, VouIdCode } from '@src/store/enums';
import dayjs from '@src/utils/day';
import { defaultDateFormat } from '@src/utils/keys';
import { getLoadBalanceDetail } from '@src/store/slice/balanceUploadDetailSlice';
import { getDetailsPageList } from '@src/store/slice/autoUploadInstructionsSlice';
import PropTypes from 'prop-types';
import CustomPagination from '@src/components/CustomPagination';

const OrderSummaryBalanceModal = ({
  modalVisible,
  handleModalVisible,
  isPending = false,
  isAuto = false,
  isCompliments = false,
  isMarket = false,
}) => {
  const { selectedOrder } = useSelector((state) => state?.order);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  let vouId;
  if (isMarket) {
    vouId = VouIdCode.Market;
  } else if (isCompliments) {
    vouId = VouIdCode.Compliments;
  } else {
    vouId = VouIdCode.Restaurant;
  }

  const dispatch = useDispatch();
  const [data, setData] = React.useState({
    items: { list: [] },
    pagedProperty: {
      currentPage: 1,
      pageSize: 1,
      totalCount: 1,
    },
  });

  const invoiceMonthText = monthsList
    .filter((month) => month.value === selectedOrder?.invoiceMonth)
    .reduce((acc, month) => month.text, '');

  const getPageList = useCallback(
    async (pageNumber, pageSize) => {
      const action = await dispatch(
        orderDetailsGetPageList({
          body: { vouId: vouId, orderId: selectedOrder?.id, showOnlyPriced: true },
          pageNumber,
          pageSize,
        }),
      );
      if (orderDetailsGetPageList.fulfilled.match(action)) {
        setData({
          items: { list: action?.payload?.data?.items },
          pagedProperty: action?.payload?.data?.pagedProperty,
        });
      }
    },
    [dispatch, selectedOrder?.id],
  );

  const pendingApprovedList = useCallback(
    async (pageNumber, pageSize) => {
      const action = await dispatch(
        getLoadBalanceDetail({
          body: {
            vouId: vouId,
            loadBalanceId: selectedOrder?.id,
            customerId: selectedCustomer?.customerId,
          },
          pageNumber,
          pageSize,
        }),
      );
      if (getLoadBalanceDetail.fulfilled.match(action)) {
        setData({
          items: { list: action?.payload?.data?.items },
          pagedProperty: action?.payload?.data?.pagedProperty,
        });
      }
    },
    [dispatch, selectedOrder?.id, selectedCustomer?.customerId],
  );

  const autoApprovedList = useCallback(async () => {
    const body = {
      autoLoadingId: 0,
      integrationId: selectedOrder?.id,
      customerId: selectedCustomer?.customerId,
      showOnlyPriced: true,
      vouId: vouId,
    };
    const action = await dispatch(getDetailsPageList(body));
    if (getDetailsPageList.fulfilled.match(action)) {
      setData({
        items: { list: action?.payload?.data?.items },
        pagedProperty: action?.payload?.data?.pagedProperty,
      });
    }
  }, [dispatch, selectedOrder?.id, selectedCustomer?.customerId]);

  const handlePendingTableChange = async (pageNumber, pageSize) => {
    await pendingApprovedList(pageNumber, pageSize);
  };

  const handleAutoTableChange = async (pageNumber, pageSize) => {
    await autoApprovedList(pageNumber, pageSize);
  };

  const handleDefaultTableChange = async (pageNumber, pageSize) => {
    await getPageList(pageNumber, pageSize);
  };

  useEffect(async () => {
    if (modalVisible) {
      if (isPending) {
        await handlePendingTableChange(1, 0);
      } else if (isAuto) {
        await handleAutoTableChange(1, 0);
      } else {
        await handleDefaultTableChange(1, 0);
      }
    }
  }, [dispatch, getPageList, modalVisible]);

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      setData({
        items: { list: [] },
        pagedProperty: { currentPage: 1, totalCount: 1, pageSize: 1 },
      });
      handleModalVisible(false);
    },
    [handleModalVisible, setData],
  );

  const columnsMonthly = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: <Text t="cardNumber" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 130,
    },
    {
      title: <Text t="userName" />,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (text, record) => {
        return `${record?.name || ''} ${record?.surname || ''}`;
      },
    },
    {
      title: <Text t="address" />,
      dataIndex: 'addressText',
      key: 'addressText',
      width: 350,
    },
    {
      title: <Text t="totalPrice" />,
      dataIndex: 'price',
      key: 'price',
      width: 90,
      align: 'right',
      render: (text) => <span>{text ? `${moneyFormat(text, false)}` : '0'}</span>,
    },
  ];

  const columnsDaily = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: <Text t="cardNumber" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 130,
    },
    {
      title: <Text t="userName" />,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (text, record) => {
        return `${record?.name || ''} ${record?.surname || ''}`;
      },
    },
    {
      title: <Text t="address" />,
      dataIndex: 'addressText',
      key: 'addressText',
      width: 350,
    },
    {
      title: <Text t="dailyPrice" />,
      dataIndex: 'dailyPrice',
      key: 'dailyPrice',
      width: 90,
      align: 'right',
      render: (text) => <span>{text ? `${moneyFormat(text, false)}` : '0'}</span>,
    },
    {
      title: <Text t="day" />,
      width: 50,
      dataIndex: 'dayCount',
      key: 'dayCount',
      align: 'center',
    },
    {
      title: <Text t="totalPrice" />,
      dataIndex: 'price',
      key: 'price',
      width: 90,
      align: 'right',
      render: (text) => <span>{text ? `${moneyFormat(text, false)}` : '0'}</span>,
    },
  ];

  const renderDateInfo = () => {
    if (isAuto) {
      return (
        <>
          <Text t="autoActivationDate" />:{' '}
          <span>
            {' '}
            <Text t="ofEachMonth" /> {selectedOrder?.startDay}
            <Text t="day2" />
          </span>
        </>
      );
    }
    return (
      <>
        <Text t="firstValidityDate" />:{' '}
        {selectedOrder?.activationDate && (
          <span>{dayjs(selectedOrder?.activationDate).format(defaultDateFormat)}</span>
        )}
      </>
    );
  };

  const renderMonthInfo = () => {
    if (isAuto) {
      return (
        <>
          <Text t="autoconfirmEveryMonth" />:{' '}
          <span>
            {selectedOrder?.confirmEveryMonth === 0
              ? <Text t="iDonTWantToApproveEveryMonth" />
              : <Text t="iWantToApproveEveryMonth" />}
          </span>
        </>
      );
    }
    return (
      <>
        <Text t="invoiceMonth" />: <span>{invoiceMonthText}</span>
      </>
    );
  };

  const renderEInvoiceInfo = () =>
    selectedOrder?.eInvoiceInformation &&
    !isAuto && (
      <>
        <Text t="eInvoiceAdditionalInfo" />: <span>{selectedOrder?.eInvoiceInformation}</span>
      </>
    );

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      if (isPending) {
        await handlePendingTableChange(pageNumber, pageSize);
      } else if (isAuto) {
        await handleAutoTableChange(pageNumber, pageSize);
      } else {
        await handleDefaultTableChange(pageNumber, pageSize);
      }
    },
    [getPageList],
  );

  return (
    <CustomModal
      className="order-summary-balance-modal"
      width={1116}
      maskClosable={false}
      footer={false}
      title={useText(isCompliments || isMarket ? 'orderDetail' : 'yourOrderSummary')}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <div className="summary-info-container">
        <div
          className="summary-info-content"
          style={{ justifyContent: isCompliments || isMarket ? 'center' : 'flex-end' }}
        >
          {isCompliments || isMarket ? null : (
            <div className="summary-info">
              <p>{renderDateInfo()}</p>
              <p>{renderMonthInfo()}</p>
              {renderEInvoiceInfo() && <p>{renderEInvoiceInfo()}</p>}
            </div>
          )}
          <div
            className="summary-info-box box-1"
            style={{ backgroundColor: isCompliments || isMarket ? '#f2f4f5' : '#e9ecef' }}
          >
            <p>
              <Text t="totalCards" />:
            </p>
            <h5 style={{ color: isCompliments || isMarket ? '#f66301' : '#f72717' }}>
              {selectedOrder?.totalCount}
            </h5>
          </div>
          <div
            className="summary-info-box box-2"
            style={{ backgroundColor: isCompliments || isMarket ? '#f2f4f5' : '#e9ecef' }}
          >
            <p>
              <Text t="totalBalanceLoad" />:
            </p>
            <h5 style={{ color: isCompliments || isMarket ? '#f66301' : '#f72717' }}>
              {moneyFormat(selectedOrder?.totalPrice, true)}
            </h5>
          </div>
        </div>
        {isCompliments || isMarket ? (
          <div
            className="summary-info"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 20,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            <p>
              <div className="gift-summary-text">{renderDateInfo()}</div>
            </p>
            <p>
              <div className="gift-summary-text">{renderMonthInfo()}</div>
            </p>
            {renderEInvoiceInfo() && (
              <p>
                <div className="gift-summary-text">{renderEInvoiceInfo()}</div>
              </p>
            )}
          </div>
        ) : null}
        <div className="summary-list-content">
          <CustomTable
            className="balance-list-table"
            pagination={false}
            dataSource={data?.items?.list}
            columns={selectedOrder.periodType === 1 ? columnsMonthly : columnsDaily}
            rowKey={(record) => `card-order-${record?.id || record?.name}`}
          />
        </div>

        {isCompliments || isMarket ? (
          <div
            className="order-summary-table-footer"
            style={{ marginTop: 20, justifyContent: 'center', alignItems: 'center' }}
          >
            <CustomPagination
              current={data?.pagedProperty?.currentPage}
              defaultCurrent={1}
              pageSize={data?.pagedProperty?.pageSize}
              total={data?.pagedProperty?.totalCount}
              onChange={async (pageNumber, pageSize) => {
                await handleTableChange(pageNumber, pageSize);
              }}
            />
          </div>
        ) : null}
      </div>
    </CustomModal>
  );
};
OrderSummaryBalanceModal.propTypes = {
  modalVisible: PropTypes.any,
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  isPending: PropTypes.bool,
  isAuto: PropTypes.bool,
};
export default OrderSummaryBalanceModal;
